import React, { useCallback, useEffect, useRef, useState } from 'react';
import { StyleCollectionOwnProps } from 'configuration-schemas/ownProps';
import { StandardCard } from 'src/components/StandardCardView/StandardCard/StandardCard';
import { styles as CardStyles } from 'src/components/StandardCardView/StandardCardView.styles';
import { styles } from 'src/pages/AssortmentBuild/StyleCollection/StyleCollection.styles';
import Subheader from 'src/components/Subheader/Subheader.container';
import { InputGeneric, Overlay } from 'src/common-ui';
import { useSelector } from 'react-redux';
import { isViewDefnLoaded } from 'src/dao/tenantConfigClient';
import { KeyFinancialsAccordion } from 'src/common-ui/components/Macros/KeyFinancial/KeyFinancialsAccordion';
import {
  selectCollectionDataState,
  selectFormattedCollectionData,
  selectFormattedItemsData,
  selectKeyFinancialsData,
  selectItemsDataState,
  selectViewDefnState,
  selectChoiceProductivityData,
} from './StyleCollection.selectors';
import { isDataLoaded } from 'src/services/pivotServiceCache';
import { ContainerPayload, HistoryPayload } from 'src/components/RightContainer/RightContainer.slice';
import { ChoiceProductivityAccordion } from 'src/common-ui/components/Macros/ChoiceProductivity/ChoiceProductivityAccordion';
import { AppState } from 'src/store';
import { MacrosProps } from 'src/common-ui/components/Macros/Macros.types';
import { first } from 'lodash';

// TODO: these types were a direct copy of types in StandardCardView for buttons
// abstract + consolidate this component logic + types for the scrolling buttons
type Direction = -1 | 1;
const LEFT: Direction = -1;
const RIGHT: Direction = 1;
const SCROLLSPEED = 400;
const CARDWIDTH = 254;

interface StyleCollectionProps extends StyleCollectionOwnProps {}

export function StyleCollection({ hideTitle, showFlowStatus, title }: StyleCollectionProps) {
  const scrollTimerRef = useRef<NodeJS.Timeout | null>(null);
  const detailCardsRef = useRef<HTMLDivElement>(null);

  const viewDefnState = useSelector(selectViewDefnState);
  const collectionDataState = useSelector(selectCollectionDataState);
  const itemsDataState = useSelector(selectItemsDataState);
  const itemsData = useSelector(selectFormattedItemsData);

  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);

  const keyFinancials = useSelector(selectKeyFinancialsData);
  const choiceProductivity = useSelector<AppState, MacrosProps[]>((state) =>
    selectChoiceProductivityData(state, selectedItemId),
  );
  const { image, description, name } = useSelector(selectFormattedCollectionData);
  const collectionDataLoading = !isDataLoaded(collectionDataState);
  const loading = !isViewDefnLoaded(viewDefnState) || collectionDataLoading || !isDataLoaded(itemsDataState);

  useEffect(() => {
    // config fetch is triggered in epic
    return () => {
      if (scrollTimerRef.current) {
        clearInterval(scrollTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    // once data is ready trigger selection of first item
    const selection = first(itemsData);
    if (itemsData.length > 0 && selection) {
      const selectedItemId = selection.leafId ?? selection.id;
      setSelectedItemId(selectedItemId);
    }
  }, [itemsData]);

  const mouseEnter = useCallback((direction: Direction, max: number) => {
    if (scrollTimerRef.current) {
      clearInterval(scrollTimerRef.current);
    }

    scrollTimerRef.current = setInterval(() => {
      if (detailCardsRef.current) {
        const currentScrollLeft = detailCardsRef.current.scrollLeft;
        const nextScrollLeft = Math.min(Math.max(currentScrollLeft + direction * CARDWIDTH, 0), max);
        detailCardsRef.current.scrollTo({ left: nextScrollLeft });
      }
    }, SCROLLSPEED);
  }, []);

  const mouseLeave = useCallback(() => {
    if (scrollTimerRef.current) {
      clearInterval(scrollTimerRef.current);
    }
  }, []);

  const handleCardClick = useCallback((item: ContainerPayload) => {
    // expect leafId or styleId
    setSelectedItemId((item as HistoryPayload).id);
  }, []);

  return (
    <section className={styles}>
      <Overlay type="loading" fitParent={true} visible={loading} />
      <Subheader title={title} showSearch={true} showFlowStatus={showFlowStatus} hideTitle={hideTitle} />
      <div className="collection-info">
        <img src={image} />
        <div className="internal-info-container">
          <div className="labels">
            <InputGeneric
              containerClassName="label"
              editable={false}
              autoFocus={false}
              value={name}
              valid={true}
              dataQa="style-collection-name"
            />
            <InputGeneric
              containerClassName="label"
              editable={false}
              autoFocus={false}
              value={description}
              valid={true}
              dataQa="style-collection-description"
            />
          </div>
          <div className="macros">
            <KeyFinancialsAccordion dataLoaded={true} macros={keyFinancials} />
            {choiceProductivity.length > 0 ? (
              <ChoiceProductivityAccordion dataLoaded={true} macros={choiceProductivity} />
            ) : null}
          </div>
        </div>
      </div>
      <header className="items-header">
        <span>Items from Collection</span>
      </header>
      <div className="detail-cards-container" ref={detailCardsRef}>
        <button
          className={CardStyles.hoverButton}
          style={{ left: 0 }}
          onMouseEnter={() => mouseEnter(LEFT, CARDWIDTH * (itemsData.length - 1))}
          onMouseLeave={mouseLeave}
        >
          <i className="far fa-angle-left" />
        </button>
        <button
          className={CardStyles.hoverButton}
          style={{ right: 0 }}
          onMouseEnter={() => mouseEnter(RIGHT, CARDWIDTH * (itemsData.length - CARDWIDTH / CARDWIDTH))}
          onMouseLeave={() => mouseLeave()}
        >
          <i className="far fa-angle-right" />
        </button>
        <div className="detail-cards">
          {itemsData.length > 0 ? (
            <div className="internal-card-container">
              {itemsData.map((card, index) => {
                const isSelected = card.leafId === selectedItemId || card.id === selectedItemId;
                return (
                  <StandardCard
                    key={index}
                    {...card}
                    onItemClick={handleCardClick}
                    ignoreSelectionStyles={true}
                    isSelected={isSelected}
                  />
                );
              })}
            </div>
          ) : (
            <div className="no-data">No Data</div>
          )}
        </div>
      </div>
    </section>
  );
}
